<script setup>
import { ref, computed, watch, defineProps, onMounted, onBeforeUnmount } from "vue";
import Plotly from "plotly.js-dist";
import { getAllWithToken } from "../../../lib/fetch-api";

const props = defineProps({
  selectedIds: {
    type: Array,
    default: () => [],
  },

  matchSession: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["close-matches", "matches-updated"]);

const plotData = ref([]);
const layout = ref({});
const useRelativeIntensity = ref(false);
const reverseXAxis = ref(false);
const useWavelength = ref(false);
const wavelengthToggleAvailable = ref(false);
const selectedMatchId = ref(null);
const visibleXRange = ref(null);
const isRangeMatching = ref(false);
const plotContainer = ref(null); // Ref for the chart container element
let resizeObserver = null;

const normalizeCategoryName = (value) =>
  String(value || "").trim().toLowerCase();

const isNanoFtirCategoryName = (value) => {
  const category = normalizeCategoryName(value);
  return category.includes("nano") && category.includes("ftir");
};

const isFtirCategoryName = (value) => {
  const category = normalizeCategoryName(value);
  return category.includes("ftir") && !isNanoFtirCategoryName(category);
};

const isWavenumberCategory = (value) =>
  isNanoFtirCategoryName(value) || isFtirCategoryName(value);

const wavenumberToWavelength = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }

  return 10000 / numericValue;
};

const wavelengthToWavenumber = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }

  return 10000 / numericValue;
};

const getAxisLabels = (files, displayWavelength = false) => {
  const categories = [
    ...new Set(files.map((file) => normalizeCategoryName(file.category)).filter(Boolean)),
  ];

  if (displayWavelength) {
    return {
      x: "Wavelength (µm)",
      y:
        categories.length === 1 && isNanoFtirCategoryName(categories[0])
          ? "Nano-FTIR Phase (a.u.)"
          : categories.length === 1 && isFtirCategoryName(categories[0])
          ? "Reflectance (a.u.)"
          : "Signal (a.u.)",
    };
  }

  if (categories.length !== 1) {
    return {
      x: "Wavenumber / Raman shift (cm⁻¹)",
      y: "Signal (a.u.)",
    };
  }

  const category = categories[0];

  if (category === "raman") {
    return {
      x: "Raman shift (cm⁻¹)",
      y: "Intensity (a.u.)",
    };
  }

  if (isNanoFtirCategoryName(category)) {
    return {
      x: "Wavenumber (cm⁻¹)",
      y: "Nano-FTIR Phase (a.u.)",
    };
  }

  if (isFtirCategoryName(category)) {
    return {
      x: "Wavenumber (cm⁻¹)",
      y: "Reflectance (a.u.)",
    };
  }

  return {
    x: "Spectral position",
    y: "Signal (a.u.)",
  };
};

const visibleRangeForDisplay = computed(() => {
  if (!visibleXRange.value) {
    return null;
  }

  if (!useWavelength.value) {
    return {
      min: visibleXRange.value.min,
      max: visibleXRange.value.max,
    };
  }

  const first = wavenumberToWavelength(visibleXRange.value.min);
  const second = wavenumberToWavelength(visibleXRange.value.max);

  if (!Number.isFinite(first) || !Number.isFinite(second)) {
    return null;
  }

  return {
    min: Math.min(first, second),
    max: Math.max(first, second),
  };
});

const visibleRangeUnit = computed(() =>
  useWavelength.value ? "µm" : "cm⁻¹"
);

const scaleToUnitRange = (values) => {
  let minimum = Infinity;
  let maximum = -Infinity;

  values.forEach((value) => {
    const numericValue = Number(value);

    if (Number.isFinite(numericValue)) {
      minimum = Math.min(minimum, numericValue);
      maximum = Math.max(maximum, numericValue);
    }
  });

  if (!Number.isFinite(minimum) || !Number.isFinite(maximum)) {
    return values;
  }

  const range = maximum - minimum;

  if (range === 0) {
    return values.map(() => 0);
  }

  return values.map((value) => {
    const numericValue = Number(value);

    return Number.isFinite(numericValue) ? (numericValue - minimum) / range : value;
  });
};

const handlePlotRelayout = (eventData) => {
  if (!eventData) {
    return;
  }

  // Plotly sends this when the user resets the view.
  if (eventData["xaxis.autorange"] !== undefined) {
    visibleXRange.value = null;
    return;
  }

  let firstValue;
  let secondValue;

  if (Array.isArray(eventData["xaxis.range"])) {
    firstValue = Number(eventData["xaxis.range"][0]);
    secondValue = Number(eventData["xaxis.range"][1]);
  } else {
    firstValue = Number(eventData["xaxis.range[0]"]);
    secondValue = Number(eventData["xaxis.range[1]"]);
  }

  if (!Number.isFinite(firstValue) || !Number.isFinite(secondValue) || firstValue === secondValue) {
    return;
  }

  if (useWavelength.value) {
    const firstWavenumber = wavelengthToWavenumber(firstValue);
    const secondWavenumber = wavelengthToWavenumber(secondValue);

    if (!Number.isFinite(firstWavenumber) || !Number.isFinite(secondWavenumber)) {
      return;
    }

    visibleXRange.value = {
      min: Math.min(firstWavenumber, secondWavenumber),
      max: Math.max(firstWavenumber, secondWavenumber),
    };
    return;
  }

  visibleXRange.value = {
    min: Math.min(firstValue, secondValue),
    max: Math.max(firstValue, secondValue),
  };
};

const formatXValue = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "";
  }

  if (useWavelength.value) {
    return numericValue.toFixed(2);
  }

  return Number.isInteger(numericValue) ? numericValue.toFixed(0) : numericValue.toFixed(1);
};

const setupPlot = async () => {
  const unknownId = props.matchSession?.unknown?.id ?? null;

  const isMatchComparison = selectedMatchId.value !== null && unknownId !== null;

  const idsToPlot = isMatchComparison ? [unknownId, selectedMatchId.value] : props.selectedIds;

  if (!Array.isArray(idsToPlot) || idsToPlot.length === 0) {
    plotData.value = [];

    const plotElement = document.getElementById("plotly-chart");

    if (plotElement) {
      Plotly.purge(plotElement);
    }

    return;
  }

  const fileIds = idsToPlot.join(",");
  const apiUrl = `spectfiles/draw?ids=${fileIds}`;
  const response = await getAllWithToken(apiUrl, null);

  if (!response.isSuccess) {
    alert(response.message || "Failed to fetch files.");
    return;
  }

  const returnedFiles = Array.isArray(response.body) ? response.body : [];

  const filesById = new Map(returnedFiles.map((file) => [file.id, file]));

  const filesToPlot = idsToPlot.map((id) => filesById.get(id)).filter(Boolean);

  if (filesToPlot.length === 0) {
    alert("Selected files not found.");
    return;
  }

  const supportsWavelength =
    filesToPlot.length > 0 &&
    filesToPlot.every(
      (file) =>
        isWavenumberCategory(file.category) &&
        Array.isArray(file.data) &&
        file.data.every((point) => Number(point.x) > 0)
    );

  wavelengthToggleAvailable.value = supportsWavelength;

  if (!supportsWavelength) {
    useWavelength.value = false;
  }

  const axisLabels = getAxisLabels(filesToPlot, useWavelength.value);
  const shouldUseRelativeIntensity = useRelativeIntensity.value || isMatchComparison;
  const hasMatchResults =
    Array.isArray(props.matchSession?.matches) && props.matchSession.matches.length > 0;

  let xAxisRangeSettings;

  if (visibleXRange.value) {
    const firstDisplayed = useWavelength.value
      ? wavenumberToWavelength(visibleXRange.value.min)
      : visibleXRange.value.min;

    const secondDisplayed = useWavelength.value
      ? wavenumberToWavelength(visibleXRange.value.max)
      : visibleXRange.value.max;

    const displayedMin = Math.min(firstDisplayed, secondDisplayed);
    const displayedMax = Math.max(firstDisplayed, secondDisplayed);

    xAxisRangeSettings = {
      range: reverseXAxis.value
        ? [displayedMax, displayedMin]
        : [displayedMin, displayedMax],
      autorange: false,
    };
  } else {
    xAxisRangeSettings = {
      autorange: reverseXAxis.value ? "reversed" : true,
    };
  }

  plotData.value = filesToPlot.map((file) => {
    const originalYValues = file.data.map((point) => point.y);

    const displayedYValues = shouldUseRelativeIntensity
      ? scaleToUnitRange(originalYValues)
      : originalYValues;

    const basicName = file.folder ? `${file.description} — ${file.folder}` : file.description;

    let traceName = basicName;

    if (isMatchComparison) {
      traceName = file.id === unknownId ? `Unknown — ${basicName}` : `Match — ${basicName}`;
    }

    return {
      x: file.data.map((point) =>
        useWavelength.value ? wavenumberToWavelength(point.x) : point.x
      ),
      y: displayedYValues,
      mode: "lines",
      type: "scatter",
      name: traceName,
      yaxis: "y",
    };
  });

  layout.value = {
    title: "Files Data Plot",

    margin: {
      l: 85,
      r: hasMatchResults ? 320 : 60,
      t: 70,
      b: 85,
    },

    legend: {
      x: 1.02,
      xanchor: "left",
      y: 1,
      yanchor: "top",
    },

    xaxis: {
      title: {
        text: axisLabels.x,
        standoff: 12,
      },
      automargin: true,
      showgrid: true,
      zeroline: false,
      ...xAxisRangeSettings,
    },

    yaxis: {
      title: {
        text: shouldUseRelativeIntensity ? "Relative intensity (0–1)" : axisLabels.y,
        standoff: 12,
      },
      automargin: true,
      showline: false,
    },

    autosize: true,
  };

  const plotElement = document.getElementById("plotly-chart");

  if (!plotElement) {
    return;
  }

  await Plotly.newPlot(plotElement, plotData.value, layout.value, {
    displayModeBar: false,
    responsive: true,
  });

  if (typeof plotElement.removeListener === "function") {
    plotElement.removeListener("plotly_relayout", handlePlotRelayout);
  }

  plotElement.on("plotly_relayout", handlePlotRelayout);
};

// Watch for changes in selectedIds and update the plot.
watch(
  () => props.selectedIds,
  () => {
    setupPlot();
  },
  {
    immediate: true,
    deep: true,
  }
);

watch(
  () => props.matchSession,
  (newSession, oldSession) => {
    const newUnknownId = newSession?.unknown?.id ?? null;

    const oldUnknownId = oldSession?.unknown?.id ?? null;

    // A completely new unknown spectrum starts
    // with a fresh range and no selected reference.
    if (newUnknownId !== oldUnknownId) {
      selectedMatchId.value = null;
      visibleXRange.value = null;
    } else if (selectedMatchId.value !== null) {
      const selectedMatchStillExists =
        Array.isArray(newSession?.matches) &&
        newSession.matches.some((match) => match.id === selectedMatchId.value);

      if (!selectedMatchStillExists) {
        selectedMatchId.value = null;
      }
    }

    setupPlot();
  },
  {
    deep: true,
  }
);

const handleDownload = () => {
  const plotElement = document.getElementById("plotly-chart");
  if (plotElement) {
    Plotly.toImage(plotElement, { format: "png", height: 600, width: 800 }).then((url) => {
      const link = document.createElement("a");
      link.href = url;
      link.download = "plot.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  }
};

const toggleRelativeIntensity = () => {
  useRelativeIntensity.value = !useRelativeIntensity.value;
  setupPlot();
};

const toggleReverseXAxis = () => {
  reverseXAxis.value = !reverseXAxis.value;
  setupPlot();
};

const toggleWavelength = () => {
  if (!wavelengthToggleAvailable.value) {
    return;
  }

  useWavelength.value = !useWavelength.value;
  setupPlot();
};

const selectMatch = (match) => {
  selectedMatchId.value = match.id;
  setupPlot();
};

const matchVisibleRange = async () => {
  const unknownId = props.matchSession?.unknown?.id;

  if (!unknownId || !visibleXRange.value || isRangeMatching.value) {
    return;
  }

  isRangeMatching.value = true;

  try {
    const parameters = new URLSearchParams({
      x_min: String(visibleXRange.value.min),
      x_max: String(visibleXRange.value.max),
    });

    const response = await getAllWithToken(
      `spectfiles/match-public/${unknownId}?${parameters.toString()}`,
      null
    );

    if (!response.isSuccess) {
      alert(response.message || response.body || "Failed to match the visible range.");
      return;
    }

    const matches = Array.isArray(response.body?.matches) ? response.body.matches : [];

    if (matches.length === 0) {
      alert("No suitable public matches were found " + "within the visible range.");
      return;
    }

    emit("matches-updated", response.body);
  } catch (error) {
    console.error("Visible-range matching failed:", error);

    alert("Failed to match the visible range.");
  } finally {
    isRangeMatching.value = false;
  }
};

const closeMatchResults = () => {
  selectedMatchId.value = null;
  visibleXRange.value = null;
  emit("close-matches");
};

// Use a ResizeObserver to trigger Plotly resize when the container changes.
onMounted(() => {
  if (plotContainer.value) {
    resizeObserver = new ResizeObserver(() => {
      Plotly.Plots.resize(plotContainer.value);
    });
    resizeObserver.observe(plotContainer.value);
  }
});
onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});
</script>

<template>
  <!-- Remove min-h-screen and use h-full to fill the available space -->
  <div class="p-8 h-full max-w-full mx-auto bg-white-50 rounded-lg flex flex-col">
    <!-- Chart Container -->
    <div class="flex-1 overflow-hidden">
      <div ref="plotContainer" class="relative h-full rounded-lg bg-white p-6">
        <div id="plotly-chart" class="w-full h-full"></div>
        <div
          v-if="
            matchSession && Array.isArray(matchSession.matches) && matchSession.matches.length > 0
          "
          class="absolute bottom-6 right-6 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
        >
          <div class="flex items-start justify-between border-b border-gray-200 px-3 py-2">
            <div class="min-w-0">
              <h3 class="text-sm font-semibold text-gray-800">Matches</h3>

              <p class="truncate text-xs text-gray-500">
                Unknown:
                {{ matchSession.unknown.description || matchSession.unknown.name }}
              </p>

              <div v-if="visibleXRange" class="mt-2">
                <p class="mb-1 text-[11px] text-gray-500">
                  Visible range:
                  {{ formatXValue(visibleRangeForDisplay?.min) }}
                  –
                  {{ formatXValue(visibleRangeForDisplay?.max) }}
                  {{ visibleRangeUnit }}
                </p>

                <button
                  type="button"
                  :disabled="isRangeMatching"
                  class="w-full rounded border border-blue-300 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                  @click="matchVisibleRange"
                >
                  {{ isRangeMatching ? "Matching range..." : "Match visible range" }}
                </button>
              </div>
            </div>

            <button
              type="button"
              title="Close matching results"
              class="ml-2 text-lg leading-none text-gray-400 hover:text-gray-700"
              @click="closeMatchResults"
            >
              ×
            </button>
          </div>

          <div class="max-h-56 overflow-y-auto">
            <button
              v-for="(match, index) in matchSession.matches"
              :key="match.id"
              type="button"
              class="w-full border-b border-gray-100 px-3 py-2 text-left transition-colors last:border-b-0"
              :class="
                selectedMatchId === match.id
                  ? 'bg-blue-50 ring-1 ring-inset ring-blue-300'
                  : 'bg-white hover:bg-gray-50'
              "
              :aria-pressed="selectedMatchId === match.id"
              @click="selectMatch(match)"
            >
              <div class="flex items-start gap-2">
                <span class="w-5 shrink-0 text-xs font-semibold text-gray-400">
                  {{ index + 1 }}
                </span>

                <div class="min-w-0 flex-1">
                  <p class="truncate text-xs font-medium text-gray-800">
                    {{ match.description || match.name }}
                  </p>

                  <p class="mt-0.5 text-[11px] text-gray-500">
                    {{ match.subcategory || "Unclassified" }}
                  </p>
                </div>

                <span class="shrink-0 text-xs font-semibold text-blue-700">
                  {{ Number(match.similarity).toFixed(3) }}
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Controls (centered) -->
    <div class="flex flex-wrap justify-center gap-4 mt-4">
      <button
        @click="toggleRelativeIntensity"
        :disabled="selectedMatchId !== null"
        title="Scale each plotted spectrum to a 0–1 range for easier peak-position comparison"
        class="rounded-lg bg-green-600 px-6 py-3 text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ useRelativeIntensity ? "Show Original" : "Normalize Spectra" }}
      </button>
      <button
        @click="toggleReverseXAxis"
        class="rounded-lg bg-indigo-600 px-6 py-3 text-white transition-colors hover:bg-indigo-700"
      >
        {{ reverseXAxis ? "Restore X-axis" : "Reverse X-axis" }}
      </button>
      <button
        @click="toggleWavelength"
        :disabled="!wavelengthToggleAvailable"
        :title="
          wavelengthToggleAvailable
            ? 'Switch between wavenumber and wavelength display'
            : 'Wavelength conversion is available for FTIR and Nano-FTIR spectra'
        "
        class="rounded-lg bg-purple-600 px-6 py-3 text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ useWavelength ? "Wavenumber (cm-1)" : "Wavelength (µm)" }}
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Ensure the Plotly chart fills its container */
#plotly-chart {
  width: 100%;
  height: 100%;
}
</style>
